import React from 'react';
import { Card, Space, Typography, Alert, Tag } from 'antd';
import {
  ThunderboltOutlined,
  ExperimentOutlined,
  CloseCircleOutlined,
  EditOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { OptimizerSummary } from '../types';

const { Title, Text, Paragraph } = Typography;

interface OptimizerSummaryPanelProps {
  summary: OptimizerSummary;
  requestRetrigger?: boolean;
}

export const OptimizerSummaryPanel: React.FC<OptimizerSummaryPanelProps> = ({
  summary,
  requestRetrigger = false
}) => {
  if (!summary) {
    return null;
  }

  const hasContent =
    summary.what_attempted ||
    summary.what_failed ||
    summary.what_changed ||
    requestRetrigger;

  if (!hasContent) {
    return null;
  }

  return (
    <Card
      title={
        <Space>
          <ThunderboltOutlined />
          <span>Optimizer Learning Summary</span>
          {requestRetrigger && (
            <Tag icon={<ReloadOutlined />} color="processing">
              Retrigger Requested
            </Tag>
          )}
        </Space>
      }
      size="small"
      style={{ marginTop: '16px' }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Alert
          message="Self-Healing Loop Learning"
          description="This summary shows how the Optimizer LLM learned from failures and adjusted the execution plan."
          type="info"
          showIcon
          icon={<ExperimentOutlined />}
        />

        {summary.what_attempted && (
          <div>
            <Space align="start">
              <ExperimentOutlined style={{ color: '#1890ff', marginTop: '4px' }} />
              <div style={{ flex: 1 }}>
                <Text strong style={{ display: 'block', marginBottom: '4px' }}>
                  Attempted:
                </Text>
                <Paragraph style={{ marginBottom: 0, paddingLeft: '8px', borderLeft: '3px solid #1890ff' }}>
                  {summary.what_attempted}
                </Paragraph>
              </div>
            </Space>
          </div>
        )}

        {summary.what_failed && (
          <div>
            <Space align="start">
              <CloseCircleOutlined style={{ color: '#ff4d4f', marginTop: '4px' }} />
              <div style={{ flex: 1 }}>
                <Text strong style={{ display: 'block', marginBottom: '4px' }}>
                  Failed:
                </Text>
                <Paragraph style={{ marginBottom: 0, paddingLeft: '8px', borderLeft: '3px solid #ff4d4f' }}>
                  {summary.what_failed}
                </Paragraph>
              </div>
            </Space>
          </div>
        )}

        {summary.what_changed && (
          <div>
            <Space align="start">
              <EditOutlined style={{ color: '#52c41a', marginTop: '4px' }} />
              <div style={{ flex: 1 }}>
                <Text strong style={{ display: 'block', marginBottom: '4px' }}>
                  Changed:
                </Text>
                <Paragraph style={{ marginBottom: 0, paddingLeft: '8px', borderLeft: '3px solid #52c41a' }}>
                  {summary.what_changed}
                </Paragraph>
              </div>
            </Space>
          </div>
        )}

        {requestRetrigger && (
          <Alert
            message="Retrigger Requested"
            description="A loop retrigger has been requested after this loop gathers more information."
            type="warning"
            showIcon
            icon={<ReloadOutlined />}
          />
        )}
      </Space>
    </Card>
  );
};

export default OptimizerSummaryPanel;
